<template>
  <msg-box :backdrop-close="true" size="lg" @close="close">
    <template #title>
      <div class="d-flex px-2">
        {{ $t('HELP.VIDEO_TUTOS') }}
        <v-spacer></v-spacer>
        <v-btn href="https://www.youtube.com/channel/UCDb4feDzsa4fCd_vR5HZJgg" target="_blank" rel="noopener"
               color="primary" variant="flat" rounded class="mx-2 px-6">
          <v-icon start>far fa-external-link</v-icon>{{ $t('HELP.YOUTUBE_CHANNEL') }}
        </v-btn>
      </div>
    </template>

    <v-container class="pa-0" style="height: calc(90vh - 95px); flex: 1 1 auto;">
      <v-row class="ma-0" style="height: 100%;">
        <v-col cols="4" style="height: 100%; overflow-y: auto;">
          <template v-for="(categoryName, category) in categories" :key="category">
            <h4 class="text-primary text-uppercase mb-2">{{ categoryName }}</h4>
            <v-expansion-panels class="mb-8">
              <v-expansion-panel v-for="video in videos[category]" :key="video.youtubeId" @click="selectedVideo = video">
                <v-expansion-panel-title class="px-2" style="line-height: 1.3em">
                  <v-icon color="errorred" class="flex-grow-0 mr-2">fab fa-youtube</v-icon>
                  <div class="mr-1">
                    <b>{{ video.title }}<span v-if="video['label-new']" class="label-new ml-1">NEW</span></b>
                    <div v-if="video.premium" class="mt-n1 d-flex align-center" style="position: relative; bottom: -8px">
                      <premium-section icon feature="" class="mr-1 mb-.5"></premium-section>
                      <v-chip color="video.enterprise ? colors.enterprise : video.business ? colors.business : colors.team" density="compact">
                        <span v-if="video.enterprise">{{ $t('PREMIUM.ENTERPRISE_FEATURE') }}</span>
                        <span v-else-if="video.business">{{ $t('PREMIUM.BUSINESS_FEATURE') }}</span>
                        <span v-else>{{ $t('PREMIUM.PREMIUM_FEATURE') }}</span>
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  {{ video.description }}
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-col>
        <v-col cols="8" style="height: 100%">
          <iframe :src="`https://www.youtube.com/embed/${selectedVideo.youtubeId}`" frameborder="0" gesture="media"
                  allow="encrypted-media; autoplay; fullscreen"
                  :title="selectedVideo.title" style="width: 100%; height: 100%">
          </iframe>
        </v-col>
      </v-row>
    </v-container>
  </msg-box>
</template>

<script>
  /* eslint-disable max-len, vue/max-len */

  export default {
    emits: ['close'],
    data() {
      return {
        selectedVideo: null,
        colors: { team: '#278bff', business: '#3cd458', enterprise: '#606060' },
        categories: {
          starter: 'Prise en main',
          visual: 'Management Visuel',
          planification: 'Planification',
          management: 'Suivi de projet',
          dashboards: 'Tableaux de bord',
          templates: 'Modèles et sous-projets',
          business: 'Business',
        },
        videos: {
          starter: [
            {
              title: "Gérer l'axe temporel",
              description: "Comment organiser votre échelle de temps selon les particularités de votre projet. Prolonger un planning, modifier sa forme en fonction des projets, vue macro ou micro, ... : toutes les clés pour gérer parfaitement votre axe temporel.",
              youtubeId: 'AFREKaiqioY',
            },
            {
              title: "Les menus",
              description: "De nombreux menus et options sont disponibles dans notre outil collaboratif de gestion de projet. Trouvez facilement vos fonctionnalités et ne ratez rien de Bubble Plan. Vous pouvez également télécharger notre guide utilisateur en PDF pour repérer facilement toutes les fonctions.",
              youtubeId: 'DUSnyGMrYFg',
            },
            {
              title: "Les éléments de base des projets",
              description: "Apprenez comment créer vos projets à partir de bulles et de jalons et notamment dans quel cas choisir l'une ou l'autre.",
              youtubeId: 'iXZDXFxa04I',
              'label-new': true,
            },
            {
              title: "Créer une bulle ou un jalon",
              description: "Découvrez les différentes manières d'ajouter facilement de nouveaux éléments à vos projets.",
              youtubeId: 'l_4lnOPWTo4',
            },
            {
              title: "Gérer les dates et les durées des bulles",
              description: "Découvrez les différentes manières d'ajouter et modifier les dates et les durées de vos bulles et jalons.",
              youtubeId: 'NY6zdOl_w7k',
            },
            {
              title: "Gérer et organiser ses documents",
              description: "Découvrez comment intégrer vos pièces jointes et documents et suivre les livrables d'un projet.",
              youtubeId: '00VZjyWlbbU',
              'label-new': true,
            },
            {
              title: "La mise en retrait des lignes",
              description: "Cette vidéo vous fait découvrir comment segmenter votre projet en utilisant la fonction d'indentation des lignes (mise en retrait).",
              youtubeId: 'cMOnYEBiqBE',
            },
            {
              title: "La gestion des lignes de vos projets",
              description: "Choix des types de lignes, structuration du planning, réorganisation rapide et groupée de vos lignes : découvrez comment profiter au mieux des options de gestion des lignes. Certaines options sont réservées aux utilisateurs PREMIUM.",
              youtubeId: '_tOIM0HthKU',
            },
            {
              title: "Le positionnement des éléments, et les prioriser",
              description: "Tout comprendre de la logique de placement vertical des bulles et jalons, et des possibilités de modifier le positionnement automatique.",
              youtubeId: 'tt74H5zfDQQ',
              'label-new': true,
            },
            {
              title: "Les subtilités de l'axe temporel",
              description: "Apprenez comment utiliser et personnaliser l'axe temporel en mode Planning",
              youtubeId: 'asI6DWlGhks',
              'label-new': true,
            },
            {
              title: "Créer un nouveau planning sur Bubble Plan",
              description: "Découvrez comment paramétrer un nouveau planning projet et lancez tous les nouveaux projets de votre périmètre d'activité.",
              youtubeId: '5F-kdbTnqJo',
              premium: true,
              'label-new': true,
            },
            {
              title: "Supprimer un projet Bubble Plan",
              description: "Découvrez comment s'effectue la suppression définitive d'un planning projet.",
              youtubeId: 'Iq-YV0FXu44',
            },
          ],
          visual: [
            {
              title: "Exploiter pleinement la force du management visuel",
              description: "Découvrez comment utiliser et personnaliser la logique globale de management visuel et choisissez les meilleures options de suivi et de communication.",
              youtubeId: 'dthaifFvzDg',
            },
            {
              title: "Définir ce qui est visible sur vos plannings projets",
              description: "La fonction de masquage, représentée par le petit œil dans Bubble Plan, est idéale pour structurer votre projet et en gérer l'affichage. Il est essentiel de choisir ce que vous voulez montrer sur votre planning, pour qu'il soit clair et intuitif.",
              youtubeId: '7puEbVhTlGg',
            },
            {
              title: "Bien utiliser les marqueurs verticaux pour votre management visuel",
              description: "Découvrez comment mettre en place des plages de couleur sur votre planning pour mieux identifier dates clés et périodes.",
              youtubeId: 'HAMRv6wcDMI',
            },
            {
              title: "La Météo Projet, en reporting ou en suivi de projet",
              description: "Fonction intégrée dans votre Fiche Projet, la météo, liée au \"point de suivi\", permet de donner une autre dimension en terme de management visuel et de reporting sur votre projet.",
              youtubeId: 'y-x70D8zkmE',
              'label-new': true,
            },
          ],
          planification: [
            {
              title: "Les intervenants",
              description: "Affiner vos process avec les intervenants et les nombreuses utilisations que vous pouvez en faire. Découvrez les différences entre les intervenants réels et virtuels et où les retrouver.",
              youtubeId: 'K8hHyoo3ufg',
            },
            {
              title: "Organiser votre contenu avec des checklists d'actions",
              description: "Cette vidéo vous montre les possibilités pour segmenter votre projet et créer des sous-tâches ou actions.",
              youtubeId: 'NoJzNY3iwR8',
            },
            {
              title: "La gestion des actions",
              description: "Checklists, tâches, organisation des deadlines : découvrez comment fonctionne le système de gestion des actions de Bubble Plan. Créez vos tâches et détectez les impératifs, pour un suivi de projet efficace.",
              youtubeId: '0ObRhI_Ivkk',
            },
            {
              title: "La planification avancée",
              description: "Découvrez comment franchir un nouveau palier sur la gestion des actions, des intervenants ou piloter votre charge de travail, grâce à l'option Planification avancée.",
              youtubeId: 'vOqQnjHq60E',
              premium: true,
            },
            {
              title: "Les liaisons Gantt",
              description: "Découvrez comment ordonnancer votre planning avec des contraintes de réalisation, de type Gantt.",
              youtubeId: 'VUC_vrUkQnM',
            },
            {
              title: "Gérer le verrouillage d'événements inamovibles",
              description: "Dans une logique Gantt, il est souvent utile de pouvoir matérialiser des événements, des jalons, des phases qui sont imposées et ne devront pas bouger. Ces éléments incompressibles qui impactent votre projet et d'éventuels décalages.",
              youtubeId: 'NRl_JyYaKyM',
            },
            {
              title: "Aller plus loin dans l'automatisation des process sous Gantt",
              description: "Découvrez comment intégrer les contraintes projets et mettre en place des automatismes pour suivre vos impacts et piloter efficacement tous vos projets.",
              youtubeId: '0z7YbA5qKE4',
            },
            {
              title: "Planifier une alerte sur Bubble Plan",
              description: "Planifiez une alerte, notion différente des notifications, sur une bulle ou un jalon, X jours avant ou après sa date limite, pour ne plus rien louper ! Découvrez comment les programmer selon vos préférences et restez averti par e-mail.",
              youtubeId: 'z_sSCPl9Krw',
            },
            {
              title: "Gérer les notifications de ses projets",
              description: "Les notifications vous permettent d'être tenu au courant de l'avancée du projet et de rester engagé dans sa progression. Découvrez comment définir la fréquence de vos notifications et où les retrouver !",
              youtubeId: 'OFB0VwBHpF0',
            },
            {
              title: "Fonctionnement et atouts de notre mode Kanban unique",
              description: "Découvrez comment fonctionne le mode Kanban de Bubble Plan, directement dans votre outil. Comment le structurer ? Bien le personnaliser ? Dans quels cas l'appliquer ? On vous dit tout de cette approche, qui reste unique dans notre solution de gestion de projet, car à double entrée et très souple.",
              youtubeId: 'jbHao6Idnso',
            },
            {
              title: "L'impact de la fonction backlog, même en pure planification",
              description: "Découvrez à quoi peut servir le backlog pour préparer ou enrichir une logique planning. Où le trouver ? Dans quels cas l'appliquer ? Quelle personnalisation est possible ? On vous dit tout sur ce moyen utile de modélisation des projets et les bénéfices à en tirer.",
              youtubeId: '3X_LoV3b8v0',
            },
            {
              title: "Réaliser des actions groupées avec la sélection multiple",
              description: "Découvrez comment modifier plusieurs éléments d'un coup, pour plus d'efficacité et d'homogénéité dans vos projets. Comment sélectionner plusieurs éléments ? Quelles actions sont possibles ? On vous dit tout sur cette fonctionnalité qui vous fera gagner du temps.",
              youtubeId: 'S_bN_eYF1KI',
              'label-new': true,
            },
          ],
          management: [
            {
              title: "Les catégories projets",
              description: "Classer vos projets par catégorie est une manière de les identifier plus facilement et rapidement.",
              youtubeId: 'EwcJIHXGlQg',
            },
            {
              title: "La fiche projet et ses usages",
              description: "Découvrez comment exploiter la Fiche Projet, pour cadrer et suivre l'évolution de votre projet, mais également en matière de reporting. Comment la structurer ? Comprendre les indicateurs ? Exploiter le budget ? On vous dit tout sur cette fonctionnalité de cadrage et de conduite du projet.",
              youtubeId: 'Lgk7KeaNo80',
            },
            {
              title: "Modifier la vignette de son projet",
              description: "Personnaliser la vignette de vos projets est une manière de les illustrer concrètement et de les identifier plus facilement.",
              youtubeId: 'GCq6eiX8Pkc',
            },
            {
              title: "Partager un planning projet",
              description: "Outil de présentation par excellence, Bubble Plan vous propose de partager vos projets, en interne ou en externe, selon vos besoins et vos process. Via la gestion des droits de collaborateurs ou de membres du compte ou par le lien numérique dédié aux invités en lecture seule, plusieurs options de partage de vos plannings sont disponibles.",
              youtubeId: 'BvHEr6u_LdM',
            },
            {
              title: "Exporter votre planning projet",
              description: "CSV, Image, PDF, autant d'options d'export disponibles dans Bubble Plan. Certaines options sont réservées aux utilisateurs PREMIUM.",
              youtubeId: 'SkNDWwJ3Kbk',
            },
          ],
          dashboards: [
            {
              title: "Le tableau de bord personnel",
              description: "Découvrez comment lire ce tableau de bord de vos actualités personnelles et l'exploiter au quotidien pour un gain de temps avec vos échéances synthétisées.",
              youtubeId: 'nUb7HkZdGp4',
            },
            {
              title: "Le tableau de bord « planning des intervenants » et son plan de charge",
              description: "Comment optimiser la gestion des ressources du ou des projets avec un tableau de bord dédié aux équipes, intervenants et participants.",
              youtubeId: 'v7W9Ln8C_zs',
              premium: true,
            },
            {
              title: "Le tableau de bord « suivi d'avancement » pour un pilotage opérationnel et décisionnel",
              description: "Pour identifier rapidement les avancements, les retards, au sein de vos projets, profitez d'une analyse rapide de Bubble Plan, grâce au tableau de bord Suivi d'avancement. Une vue Kanban où notre outil de gestion de projet travaille pour vous afin de trier directement vos éléments projets, pour un gain immédiat en productivité.",
              youtubeId: 'TaU4kKjqIWQ',
              premium: true,
            },
            {
              title: "Le tableau de bord « gestion des actions »",
              description: "Profitez d'un pilotage multiple de vos actions grâce au tableau de bord Gestion des Actions et d'une gestion de projet opérationnelle. Créez, modifiez, validez et redistribuez les actions de vos projets depuis un seul et même endroit.",
              youtubeId: 'B-LngtanXLE',
              premium: true,
              'label-new': true,
            },
            {
              title: "Le tableau de bord « portefeuille de projets » pour le pilotage",
              description: "Découvrez comment conduire et analyser vos projets de manière macro dans cette fonctionnalité de reporting. Comment la personnaliser ? Comment analyser et interagir ? Où voir le planning macro ?",
              youtubeId: 'k1SN2CMS5TQ',
              premium: true,
            },
            {
              title: "Le filtre par défaut des tableaux de bord",
              description: "Pour une utilisation optimale de vos tableaux de bord et ne pas reprogrammer à chaque fois, le filtre par défaut de Bubble Plan sera votre meilleur allié, lors de chaque connexion.",
              youtubeId: 'IEkrmgLpZTY',
              premium: true,
            },
          ],
          templates: [
            {
              title: "Comment utiliser les modèles/templates de projet",
              description: "Pour gagner du temps sur vos projets récurrents, découvrez le fonctionnement des modèles de projet sous Bubble Plan.",
              youtubeId: 'DZyXP-iwhIM',
              premium: true,
            },
            {
              title: "Organiser des vues consolidées avec la fonctionnalité sous-projets",
              description: "Pour superviser et bien piloter l'ensemble de vos projets, il est utile de pouvoir les consulter tous en même temps, en vue Planning. Cela peut également servir pour des plannings consolidés, par catégorie, par segment, ou autres, selon vos besoins.",
              youtubeId: 'OLNvpXE4pEk',
              premium: true,
              'label-new': true,
            },
            {
              title: "Maîtriser l'import modèle et la création de projets par bloc",
              description: "Découvrez comment profiter de l'import Modèle, une forme de création de projet sous template, plus large et personnalisée. Où le trouver ? Comment bien s'en servir ? Dans quels cas choisir cette option ? On vous dit tout sur cette fonctionnalité de modélisation de projet, en mode expert.",
              youtubeId: 'VGrmPEhupn8',
              premium: true,
              business: true,
            },
          ],
          business: [
            {
              title: "La gestion des équipes",
              description: "Créez des groupes de personnes en fonction des équipes, des services, des compétences, ... De nombreuses possibilités pour venir renforcer le travail collaboratif et gagner en expertise dans le pilotage de vos projets.",
              youtubeId: 't_noMlk_mu8',
              premium: true,
              business: true,
            },
            {
              title: "La gestion de charge avancée en version business",
              description: "Visualisez la charge de travail de vos intervenants directement depuis les bulles et les tableaux de bord. Elle facilite la distribution des actions et permet d'équilibrer la charge de travail de chaque collaborateur.",
              youtubeId: 'QqIlGCXlycQ',
              premium: true,
              business: true,
            },
            {
              title: "Les vues personnalisées",
              description: "Découvrez le fonctionnement et les subtilités de la fonction Vues Personnalisées pour créer sur vos plannings projets des filtres et accès spécifiques, selon vos contraintes.",
              youtubeId: 'XFb0ljW6TR8',
              premium: true,
              business: true,
            },
            {
              title: "Le tableau de bord « agenda »",
              description: "Un tableau de bord dans lequel vos projets sont représentés sous la forme d'un agenda, très pratique en gestion de projet opérationnelle.",
              youtubeId: '6Ia__cONgQI',
              premium: true,
              business: true,
            },
            {
              title: "Les paramétrages autonomes",
              description: "Gérez les groupes d'équipes et vos problématiques d'intervenants, définissez vos intervenants virtuels ou l'affichage de vos sous-projets, ... grâce à l'interface de paramétrage de la version BUSINESS.",
              youtubeId: 'E0PKNqfz1P0',
              premium: true,
              business: true,
            },
            {
              title: "Votre reporting avec le tableau de bord Revue de Projets",
              description: "Pour programmer un rapport automatique sur vos projets, vous disposez en version Premium ENTERPRISE d'une Revue de Projets complètement paramétrable.",
              youtubeId: 'Nh6KGgVO6zc',
              premium: true,
              enterprise: true,
              'label-new': true,
            },
            {
              title: "Maîtriser l'efficacité de la bibliothèque d'éléments",
              description: "Découvrez comment profiter de la bibliothèque pour réutiliser vos éléments types et gagner en efficacité. Comment la créer ? Bien s'en servir ? Dans quels cas la déployer ? On vous dit tout sur cette fonctionnalité d'organisation des informations et d'arbitrage, pour vous et les membres du projet.",
              youtubeId: 'wSbDVN3GyOU',
              premium: true,
              business: true,
            },
            {
              title: "L'import en CSV pour l'intégration de projets déjà créés sous tableur",
              description: "Découvrez comment gagner du temps dans la mise en place de Bubble Plan comme nouveau process projet, en important vos données csv.",
              youtubeId: 'XuitP0l5fcg',
              premium: true,
              business: true,
            },
            {
              title: "L'historique projet et le versioning",
              description: "Découvrez comment exploiter l'analyse du projet selon ses états, grâce à la fonction d'historique comparatif. Comment créer des versions ? Comment analyser les données ? Dans quels cas restaurer une version ?",
              youtubeId: 'M9PoX--ZzfA',
              premium: true,
              business: true,
            },
            {
              title: "Profiter d'un Kanban multi-projets par intervenant",
              description: "Découvrez comment bénéficier d'une vue Kanban globale par ressource, via le tableau de bord Planning des Intervenants. Où le trouver ? Quel intérêt ? Dans quels cas les utiliser ? On vous dit tout sur cette fonctionnalité de pilotage opérationnelle, en mode Kanban.",
              youtubeId: 'AC2R4W0G6MI',
              premium: true,
              business: true,
            },
            {
              title: "Les liaisons start-start et finish-finish",
              description: "Découvrez comment utiliser ces dépendances avancées pour ordonnancer vos projets et gérer les impacts de vos modifications. Où les trouver ? Comment les paramétrer ? Dans quels cas les utiliser ?",
              youtubeId: 'U7k4dMDeSBs',
              premium: true,
              business: true,
              'label-new': true,
            },
            {
              title: "Le découpage de projets de longue durée",
              description: "Découvrez comment restructurer un projet long, par exemple, pluri-annuel pour gagner en fluidité. Où trouver l'outil adéquat ? Comment s'en servir ? Où retrouver ensuite les différents projets scindés ? On vous dit tout sur cette fonctionnalité d'optimisation des projets denses pour plus de lisibilité et de réactivité.",
              youtubeId: 'Du57wpty8SE',
              premium: true,
              business: true,
            },
            {
              title: "La puissance des vues de travail et de leurs filtres dynamiques",
              description: "Découvrez comment utiliser les vues de travail dans Bubble Plan, pour tirer avantage de filtres dynamiques puissants pour l'analyse rapide ou le mode Présentation. Comment les manipuler ? Bien les personnaliser ? Dans quels cas les utiliser ? Qu'allez-vous en retirer directement ? On vous dit tout sur cette fonctionnalité d'organisation des informations et d'arbitrage, pour vous et les membres du projet.",
              youtubeId: 'wiw-Tf-gq1A',
              premium: true,
              business: true,
            },
            {
              title: "Modifier d'un seul clic les ressources d'un projet",
              description: "Maîtrisez parfaitement la gestion de vos ressources dans Bubble Plan, avec l'outil d'harmonisation, qui permet de remplacer ou supprimer globalement un intervenant. Dans quels cas l'appliquer ? Comment procéder ? Choisir la bonne option ? On vous dit tout de cette logique d'organisation des ressources qui vous simplifiera sans doute la vie ; en cas de réaffectation, de turn-over ou d'absence.",
              youtubeId: '-mRWVCaDyRg',
              premium: true,
              business: true,
            },
            {
              title: "Les statuts de projet",
              description: "Découvrez l'utilité des Statuts pour organiser encore mieux vos logiques projet. Comment les intégrer ? À quoi servent-ils ? Dans quels cas les mettre en place ? On vous dit tout de cette fonctionnalité BUSINESS (et +) qui trie de manière utile, certains types de projets particuliers.",
              youtubeId: 'dW3mNniVIXw',
              premium: true,
              business: true,
            },
            {
              title: "Les classes budgétaires pour amplifier le suivi d'indicateurs financiers (ou autres)",
              description: "Découvrez l'utilisation et les bénéfices de l'option Classes budgétaires dans Bubble Plan. Dans quels cas les mettre en place ? Comment les créer ? Quel intérêt global pour l'équipe projet et son suivi ? On vous dit tout de cette logique, plus fine de suivi financier, qui permet également d'associer des problématiques d'indicateurs, de volume, qu'il faut également surveiller.",
              youtubeId: 'bM_HJck69H0',
              premium: true,
              business: true,
            },
            {
              title: "Les catégories projet et leur code couleur",
              description: "En version BUSINESS (et +), vous pouvez personnaliser les catégories projet, pour les adapter à votre charte graphique, à vos logiques et à vos critères. Où trouver l'option ? Comment s'en servir ? On vous dit tout de cette option de configuration, qui est très utile pour un environnement totalement à votre image.",
              youtubeId: 'Pxd6CgchsM8',
              premium: true,
              business: true,
            },
            {
              title: "Programmer des éléments récurrents dans votre planning",
              description: "Découvrez comment fonctionne l'option de récurrence des bulles et jalons dans Bubble Plan. Accéder à la fonctionnalité ? Paramétrer l'option ? Faire de futurs ajustements ? On vous dit tout sur cette option très pratique pour des réunions ou des missions cycliques, pour éviter de multiples duplications.",
              youtubeId: 't23pu4Yq9Ac',
              premium: true,
              business: true,
            },
          ],
        },
      };
    },
    created() {
      [this.selectedVideo] = this.videos.starter;
    },
    methods: {
      close() {
        this.$emit('close');
      },
    },
  };
</script>
